import { useEffect, useReducer, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { ServerMessage } from "./presenceTypes";
import {
  presenceReducer,
  initialPresenceState,
} from "../reducers/presenceReducer";
import { SysState, selectFeatureFlag } from "../reducers/sysReducer";

const {
  REACT_APP_WS_HOST,
  REACT_APP_WS_PORT,
  REACT_APP_WS_PROTOCOL,
  NODE_ENV,
  REACT_APP_FORCE_API_PROXY,
} = process.env;

const useProxy = REACT_APP_FORCE_API_PROXY === "true";

const webSocketBaseUrl =
  NODE_ENV === "test"
    ? "ws://localhost:8080/ws"
    : useProxy
    ? "ws://localhost:3000/wss"
    : `${REACT_APP_WS_PROTOCOL}://${REACT_APP_WS_HOST}:${REACT_APP_WS_PORT}/ws`;

const MAX_RECONNECT_ATTEMPTS = 5;

export default function usePresence(slug: string | null) {
  const [state, dispatch] = useReducer(presenceReducer, initialPresenceState);
  const [reconnectCount, setReconnectCount] = useState(0);
  const socketRef = useRef<WebSocket | null>(null);
  const webSocketDebugging = useSelector((state: { sys: SysState }) =>
    selectFeatureFlag(state, "webSocketDebugging")
  );

  useEffect(() => {
    dispatch({ type: "presence", action: "reset" });
    setReconnectCount(0);
  }, [slug]);

  useEffect(() => {
    if (!slug) return;

    if (reconnectCount >= MAX_RECONNECT_ATTEMPTS) {
      console.error(
        `Presence WebSocket: giving up on presence/${slug} after ${MAX_RECONNECT_ATTEMPTS} attempts.`
      );
      return;
    }

    if (reconnectCount > 0) {
      console.debug(
        `Presence WebSocket: reconnect attempt ${reconnectCount}/${MAX_RECONNECT_ATTEMPTS} for presence/${slug}`
      );
    }

    const handleMessage = (data: ServerMessage) => {
      if (data.type === "ping") {
        socketRef.current?.send(JSON.stringify({ type: "pong" }));
        return;
      }
      if (data.type !== "presence") return;
      switch (data.action) {
        case "snapshot":
          dispatch({ type: "presence", action: "snapshot", tree: data.tree });
          break;
        case "join":
          dispatch({
            type: "presence",
            action: "join",
            user: data.user,
            slug: data.slug,
          });
          break;
        case "evict":
          dispatch({
            type: "presence",
            action: "evict",
            user: data.user,
            slug: data.slug,
          });
          break;
      }
    };

    const url = `${webSocketBaseUrl}/presence/${slug}/`;
    console.debug(`Presence WebSocket: connecting to ${url}`);
    const socket = new WebSocket(url);
    socketRef.current = socket;

    let intentionalClose = false;

    const onOpen = (e: Event) => {
      if (webSocketDebugging)
        console.log(`Presence WebSocket: opened for presence/${slug}`, e);
    };

    const onMessage = (e: MessageEvent) => {
      if (webSocketDebugging)
        console.log(`Presence WebSocket: message from presence/${slug}`, e);
      try {
        handleMessage(JSON.parse(e.data));
      } catch (err) {
        if (err instanceof SyntaxError) {
          console.error("Presence WebSocket: invalid JSON received", e.data);
        } else {
          throw err;
        }
      }
    };

    const onError = (e: Event) => {
      console.error(`Presence WebSocket: error on presence/${slug}`, e);
    };

    const onClose = (e: CloseEvent) => {
      if (webSocketDebugging) {
        if (!e.isTrusted || !e.wasClean) {
          console.warn(
            `Presence WebSocket: closed unexpectedly for presence/${slug}`,
            e
          );
        } else {
          console.log(`Presence WebSocket: closed for presence/${slug}`, e);
        }
      }
      if (!intentionalClose) {
        setReconnectCount((n) => (n < MAX_RECONNECT_ATTEMPTS ? n + 1 : n));
      }
    };

    socket.addEventListener("open", onOpen);
    socket.addEventListener("message", onMessage);
    socket.addEventListener("error", onError);
    socket.addEventListener("close", onClose);

    return () => {
      intentionalClose = true;
      socket.removeEventListener("open", onOpen);
      socket.removeEventListener("message", onMessage);
      socket.removeEventListener("error", onError);
      socket.removeEventListener("close", onClose);
      socket.close();
      socketRef.current = null;
      if (webSocketDebugging)
        console.log(
          `Presence WebSocket: intentionally closed presence/${slug}`
        );
    };
  }, [slug, reconnectCount]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    users: state.users,
  };
}
